import React, { useEffect } from "react";
import {
  useTranslate,
  useOne,
  useUpdate,
  useApiUrl,
  useCustom,
  useCustomMutation,
} from "@refinedev/core";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Button,
  Spin,
  Divider,
  Typography,
  message,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";

type ClientSettingsEntity = {
  id: number;
  webhookUrl: string;
  messageWebhookUrl?: string;
};

type ClientEditModalProps = {
  open: boolean;
  clientId: string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

const { Text } = Typography;

export const ClientEditModal: React.FC<ClientEditModalProps> = ({
  open,
  clientId,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm();

  const { data, isLoading } = useOne({
    resource: "clients",
    id: clientId ?? "",
    queryOptions: {
      enabled: open && !!clientId,
    },
  });

  const record = data?.data;

  const {
    data: settingsResponse,
    isFetching: isSettingsFetching,
    isFetched: isSettingsFetched,
    isError: isSettingsError,
    refetch: refetchSettings,
  } = useCustom<ClientSettingsEntity>({
    url: clientId ? `${apiUrl}/clients/${clientId}/settings` : "",
    method: "get",
    errorNotification: false,
    queryOptions: {
      enabled: open && !!clientId,
      retry: false,
      queryKey: ["client-settings", clientId],
    },
  });

  const settingsEntity = settingsResponse?.data;

  const { mutate: updateClient, isLoading: isUpdating } = useUpdate();
  const { mutate: updateSettings, isLoading: isUpdatingSettings } =
    useCustomMutation();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    if (!record) return;

    form.setFieldsValue({
      name: record.name,
      scopes: record.scopes ?? [],
      isActive: record.isActive,
    });

    if (isSettingsFetched || isSettingsError) {
      form.setFieldsValue({
        webhookUrl: settingsEntity?.webhookUrl ?? "",
        messageWebhookUrl: settingsEntity?.messageWebhookUrl ?? "",
      });
    }
  }, [
    record,
    open,
    form,
    isSettingsFetched,
    isSettingsError,
    settingsEntity,
  ]);

  const handleFinish = (values: {
    name: string;
    scopes: string[];
    isActive: boolean;
    webhookUrl?: string;
    messageWebhookUrl?: string;
  }) => {
    if (!clientId) return;
    const { webhookUrl, messageWebhookUrl, name, scopes, isActive } = values;
    const trimmedWebhookUrl = webhookUrl?.trim() ?? "";
    const trimmedMessageWebhookUrl = messageWebhookUrl?.trim() ?? "";

    const handleSuccess = () => {
      form.resetFields();
      onCancel();
      onSuccess?.();
    };

    updateClient(
      {
        resource: "clients",
        id: clientId,
        values: { name, scopes, isActive },
      },
      {
        onSuccess: () => {
          updateSettings(
            {
              url: `${apiUrl}/clients/${clientId}/settings`,
              method: "put",
              values: {
                webhookUrl: trimmedWebhookUrl,
                messageWebhookUrl: trimmedMessageWebhookUrl,
              },
              errorNotification: false,
            },
            {
              onSuccess: () => {
                void refetchSettings();
                handleSuccess();
              },
              onError: () => {
                message.error(_t("clients.messages.settingsSaveError"));
              },
            },
          );
        },
      },
    );
  };

  const settingsReady = isSettingsFetched || isSettingsError;

  return (
    <Modal
      title={_t("clients.titles.edit")}
      open={open}
      onCancel={onCancel}
      width={580}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{_t("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isUpdating || isUpdatingSettings}
            disabled={!settingsReady}
            onClick={() => form.submit()}
          >
            {_t("buttons.save")}
          </Button>
        </Space>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="!mt-2"
        >
          <Form.Item
            label={_t("clients.fields.name")}
            name="name"
            rules={[
              {
                required: true,
                message: _t("fields.errors.required"),
              },
            ]}
          >
            <Input placeholder={_t("clients.fields.name")} />
          </Form.Item>
          <Form.Item
            label={_t("clients.fields.scopes")}
            name="scopes"
            rules={[
              {
                required: true,
                message: _t("fields.errors.required"),
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder={_t("clients.fields.scopes")}
              tokenSeparators={[","]}
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            label={_t("clients.fields.isActive")}
            name="isActive"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Divider className="!my-4" />
          <Text type="secondary" className="mb-3 block text-sm font-medium">
            {_t("clients.sections.settings")}
          </Text>
          {isSettingsFetching && !settingsReady ? (
            <div className="flex justify-center py-4">
              <Spin size="small" />
            </div>
          ) : (
            <>
              <Form.Item
                label={_t("clients.fields.webhookUrl")}
                name="webhookUrl"
                rules={[
                  {
                    type: "url",
                    message: _t("service-providers.errors.invalidUrl"),
                  },
                ]}
              >
                <Input
                  type="url"
                  placeholder={_t("clients.fields.webhookUrl")}
                  className="w-full"
                />
              </Form.Item>
              <Form.Item
                label={_t("clients.fields.messageWebhookUrl")}
                name="messageWebhookUrl"
                rules={[
                  {
                    type: "url",
                    message: _t("service-providers.errors.invalidUrl"),
                  },
                ]}
              >
                <Input
                  type="url"
                  placeholder={_t("clients.fields.messageWebhookUrl")}
                  className="w-full"
                />
              </Form.Item>
            </>
          )}
        </Form>
      )}
    </Modal>
  );
};
